/**
 * AntD columns for a `WigAntTable`, built from UI metadata rows, with
 * per-FIELD render overrides.
 *
 * The library's `metadataToAntdColumns` does the building: `ord` order, `hide`,
 * the label (title-cased field name when there is none), `filterable`, and a
 * renderer per `data_type` (dates without a timezone shift, Yes/No, LongText
 * clipped with a tooltip). Two things are layered on here:
 *
 *   - `width` from the row. The library sizes columns by type only; the
 *     metadata row's own width wins when it has one.
 *   - `renders[field]`: a cell that is behaviour rather than a value (a link,
 *     a tag) keeps its render in code, keyed by the field it shows — never by
 *     column position, so reordering or hiding a column in metadata cannot
 *     put a render on the wrong column.
 *
 * Which columns exist, what they are called, their order, width and
 * visibility are data; nothing here names them.
 */
import { metadataToAntdColumns } from "@warwick-investment-group/wig-react-library";

/**
 * @param {Array<object>} metadata  rows from `/metadata?table_name=`
 * @param {object} [options]
 * @param {Record<string, Function>} [options.renders] field -> AntD `render`
 * @returns {Array<object>} AntD columns
 */
export const columnsFromMetadata = (metadata, { renders = {} } = {}) => {
  const rows = Array.isArray(metadata) ? metadata : [];
  const widthOf = new Map(
    rows.filter((r) => r && r.field).map((r) => [String(r.field), r.width])
  );
  return metadataToAntdColumns(rows).map((col) => {
    const field = String(col.key ?? col.dataIndex);
    const next = { ...col };
    const width = widthOf.get(field);
    if (width != null) next.width = width;
    if (typeof renders[field] === "function") next.render = renders[field];
    return next;
  });
};

export default columnsFromMetadata;
